/**
 * Quota error formatting + central toast helper.
 *
 * Called from `apiRequest` (client.ts) on every quota-gated 4xx — surfaces
 * a single rich toast and marks the response with `quotaToastShown=true`
 * so per-page catches can skip their generic fallback via
 * `swallowQuotaToast(err)` / `swallowQuotaResult(result)`.
 */

import React from 'react';
import { toast } from 'sonner';
import { QuotaErrorToast, type QuotaToastContent } from '@/components/ui/QuotaErrorToast';
import { humanizeRelative } from '@/utils/humanize';
import { ApiError } from './client';
import { PLAN_ERROR_CODES } from './plan-errors';

interface QuotaErrorPayload {
  errorCode?: string;
  errorParams?: Record<string, unknown>;
}

function pickNumber(params: Record<string, unknown> | undefined, key: string): number | null {
  const value = params?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function pickString(params: Record<string, unknown> | undefined, key: string): string | null {
  const value = params?.[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Translate a structured quota-error payload into title/description/action.
 * Returns null when the error isn't quota-gated (caller falls through).
 */
export function formatQuotaError(payload: QuotaErrorPayload): QuotaToastContent | null {
  const { errorCode, errorParams } = payload;

  switch (errorCode) {
    case PLAN_ERROR_CODES.CREDIT_LIMIT_EXCEEDED:
    case PLAN_ERROR_CODES.DAILY_QUOTA_EXCEEDED: {
      const cost = pickNumber(errorParams, 'cost');
      const remaining = pickNumber(errorParams, 'credits_remaining');
      const resetsAt = pickString(errorParams, 'resets_at');
      const parts: string[] = [];
      if (cost !== null && remaining !== null) {
        parts.push(`This costs ${cost} credits, you have ${remaining}.`);
      } else if (remaining !== null) {
        parts.push(`You have ${remaining} credits.`);
      }
      const resetText = resetsAt ? humanizeRelative(resetsAt) : null;
      if (resetText) {
        parts.push(`Resets ${resetText}.`);
      }
      return {
        title: 'Not enough credits',
        description: parts.join(' ') || 'Out of credits for this run.',
        action: { label: 'Manage', href: '/settings' },
      };
    }
    case PLAN_ERROR_CODES.CONCURRENT_LIMIT_EXCEEDED: {
      const active = pickNumber(errorParams, 'active_jobs');
      const limit = pickNumber(errorParams, 'concurrent_limit');
      const description =
        active !== null && limit !== null
          ? `You have ${active} of ${limit} jobs in flight. Wait for one to finish.`
          : 'Wait for an in-flight job to finish before starting another.';
      return { title: 'Too many jobs running', description };
    }
    case PLAN_ERROR_CODES.NO_SUBSCRIPTION:
      return {
        title: 'No subscription',
        description: 'Pick a plan to keep generating.',
        action: { label: 'See plans', href: '/settings#plans' },
      };
    case PLAN_ERROR_CODES.SUBSCRIPTION_EXPIRED:
      return {
        title: 'Subscription expired',
        description: 'Renew to continue generating.',
        action: { label: 'Manage', href: '/settings' },
      };
    default:
      return null;
  }
}

/** Render a formatted quota toast via Sonner's custom slot. */
export function showQuotaErrorToast(content: QuotaToastContent): void {
  toast.custom(
    (toastId) => React.createElement(QuotaErrorToast, { ...content, toastId }),
    { duration: 6000 },
  );
}

/**
 * Throwing-path dedupe. Per-page catch blocks call this BEFORE their
 * generic `toast.error(...)` fallback. Returns true if the central toast
 * already fired and the caller should skip its own.
 *
 * Covers both `ApiError` (the api-client base) and `JobError` (thrown by
 * `startJob` / `startCompositeJob` / `startTextToImageJob` — see
 * `lib/api/generation.ts`). Both carry `quotaToastShown` after the api
 * client surfaces the rich quota toast.
 */
export function swallowQuotaToast(err: unknown): boolean {
  if (err instanceof ApiError) return err.quotaToastShown === true;
  if (
    err instanceof Error &&
    'quotaToastShown' in err &&
    (err as Error & { quotaToastShown?: boolean }).quotaToastShown === true
  ) {
    return true;
  }
  return false;
}

/** Result-shape parallel for consumers that don't throw (poser-import, models). */
export function swallowQuotaResult(result: { quotaToastShown?: boolean } | null | undefined): boolean {
  return result?.quotaToastShown === true;
}
